import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Loader from '../common/Loader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import axiosInstance from '../../config/axios.js';
import { Calendar, Clock, MapPin, ArrowLeft, Save } from 'lucide-react';
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const EditAttendance = () => {
  const { t } = useTranslation();
  const { attendanceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [attendance, setAttendance] = useState(null);

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    attendance_date: (v) => validators.required(v, 'Attendance date'),
    login_time:      (v) => validators.required(v, 'Login time'),
    login_city:      (v) => v ? validators.textOnly(v, 'Login city') : '',
    login_district:  (v) => v ? validators.textOnly(v, 'Login district') : '',
    login_state:     (v) => v ? validators.textOnly(v, 'Login state') : '',
    login_country:   (v) => v ? validators.textOnly(v, 'Login country') : '',
    logout_city:     (v) => v ? validators.textOnly(v, 'Logout city') : '',
    logout_district: (v) => v ? validators.textOnly(v, 'Logout district') : '',
    logout_state:    (v) => v ? validators.textOnly(v, 'Logout state') : '',
    logout_country:  (v) => v ? validators.textOnly(v, 'Logout country') : '',
  };

  const [formData, setFormData] = useState({
    attendance_date: '',
    login_time: '',
    logout_time: '',
    login_village: '',
    login_city: '',
    login_district: '',
    login_state: '',
    login_country: '',
    logout_village: '',
    logout_city: '',
    logout_district: '',
    logout_state: '',
    logout_country: ''
  });

  useEffect(() => {
    fetchAttendance();
  }, [attendanceId]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/attendance/${attendanceId}`);
      const data = response.data.data;
      setAttendance(data);
      
      // Format data for form
      setFormData({
        attendance_date: data.attendance_date ? data.attendance_date.split('T')[0] : '',
        login_time: data.login_time ? formatTimeForInput(data.login_time) : '',
        logout_time: data.logout_time ? formatTimeForInput(data.logout_time) : '',
        login_village: data.login_location?.village || '',
        login_city: data.login_location?.city || '',
        login_district: data.login_location?.district || '',
        login_state: data.login_location?.state || '',
        login_country: data.login_location?.country || '',
        logout_village: data.logout_location?.village || '',
        logout_city: data.logout_location?.city || '',
        logout_district: data.logout_location?.district || '',
        logout_state: data.logout_location?.state || '',
        logout_country: data.logout_location?.country || ''
      });
    } catch (err) {
      Alert.error('Failed to load attendance details');
      console.error(err);
      navigate('/attendance');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    const textFields = ['login_city', 'login_district', 'login_state', 'login_country',
                        'logout_city', 'logout_district', 'logout_state', 'logout_country'];
    if (textFields.includes(name)) filtered = inputFilters.lettersOnly(value);

    setFormData(prev => ({
      ...prev,
      [name]: filtered
    }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }
    
    try {
      setSaving(true);

      // Combine date and time
      const loginDateTime = formData.login_time 
        ? `${formData.attendance_date}T${formData.login_time}:00`
        : null;
      
      const logoutDateTime = formData.logout_time 
        ? `${formData.attendance_date}T${formData.logout_time}:00`
        : null;

      const updateData = {
        attendance_date: formData.attendance_date,
        login_time: loginDateTime,
        logout_time: logoutDateTime,
        login_village: formData.login_village,
        login_city: formData.login_city,
        login_district: formData.login_district,
        login_state: formData.login_state,
        login_country: formData.login_country,
        logout_village: formData.logout_village || null,
        logout_city: formData.logout_city || null,
        logout_district: formData.logout_district || null,
        logout_state: formData.logout_state || null,
        logout_country: formData.logout_country || null
      };

      await axiosInstance.put(`/attendance/${attendanceId}`, updateData);
      Alert.success('Attendance updated successfully');
      navigate('/attendance');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update attendance');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading attendance details..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!attendance) {
    return null;
  }

  return (
    <DashboardLayout>
              <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <button
                    onClick={() => navigate('/attendance')}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-buttonBg" />
                      Edit Attendance
                    </h1>
                    <p className="text-sm text-gray-600">Update attendance record for {attendance.user_name}</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-gray-500" />
                      Basic Information
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="attendance_date"
                          value={formData.attendance_date}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.attendance_date ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.attendance_date} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Login Time <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="time"
                          name="login_time"
                          value={formData.login_time}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.login_time ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.login_time} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Logout Time
                        </label>
                        <input
                          type="time"
                          name="logout_time"
                          value={formData.logout_time}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Login Location */}
                  <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-blue-500" />
                      Login Location
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Village</label>
                        <input
                          type="text"
                          name="login_village"
                          value={formData.login_village}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                        <input
                          type="text"
                          name="login_city"
                          value={formData.login_city}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.login_city ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.login_city} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">District</label>
                        <input
                          type="text"
                          name="login_district"
                          value={formData.login_district}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.login_district ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.login_district} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('forms.state')}</label>
                        <input
                          type="text"
                          name="login_state"
                          value={formData.login_state}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.login_state ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.login_state} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                        <input
                          type="text"
                          name="login_country"
                          value={formData.login_country}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.login_country ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.login_country} />
                      </div>
                    </div>
                  </div>

                  {/* Logout Location */}
                  <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-green-500" />
                      Logout Location
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Village</label>
                        <input
                          type="text"
                          name="logout_village"
                          value={formData.logout_village}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                        <input
                          type="text"
                          name="logout_city"
                          value={formData.logout_city}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.logout_city ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.logout_city} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">District</label>
                        <input
                          type="text"
                          name="logout_district"
                          value={formData.logout_district}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.logout_district ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.logout_district} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('forms.state')}</label>
                        <input
                          type="text"
                          name="logout_state"
                          value={formData.logout_state}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.logout_state ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.logout_state} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                        <input
                          type="text"
                          name="logout_country"
                          value={formData.logout_country}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.logout_country ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.logout_country} />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button type="submit" variant="primary" icon={Save} disabled={saving} loading={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/attendance')}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default EditAttendance;